import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { createApiService } from '@/services/api';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import type { SubscriptionPlan } from '@/types/subscription';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { CreditCard, RefreshCw, Download } from 'lucide-react';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import { Button } from '@/components/ui/Button';
import { exportToCSV } from '@/utils/csvExport';

const subscriptionApi = createApiService(
  import.meta.env.VITE_SUBSCRIPTION_API_BASE_URL || '/api/subscription'
);

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#10b981',
  TRIAL: '#6366f1',
  CANCELLED: '#ef4444',
  PAUSED: '#f59e0b',
  SUSPENDED: '#6b7280',
};

const PLAN_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

interface StatusCount {
  status: string;
  count: number;
}

interface PlanDistribution {
  plan: string;
  count: number;
}

interface SubscriptionEvent {
  id: string;
  userEmail: string;
  planName: string;
  status: string;
  eventType: string;
  createdAt: string;
}

interface AnalyticsState {
  statusCounts: StatusCount[];
  planDistribution: PlanDistribution[];
  funnelData: { stage: string; count: number }[];
  recentEvents: SubscriptionEvent[];
  avgDurationDays: number;
}

function getMonthRange(monthsBack: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - monthsBack);
  return {
    startDate: start.toISOString().split('T')[0] + 'T00:00:00',
    endDate: end.toISOString().split('T')[0] + 'T23:59:59',
  };
}

export const SubscriptionAnalyticsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<AnalyticsState>({
    statusCounts: [],
    planDistribution: [],
    funnelData: [],
    recentEvents: [],
    avgDurationDays: 0,
  });
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const { startDate, endDate } = getMonthRange(6);

      const [statisticsData, plans] = await Promise.all([
        subscriptionApi
          .get<Record<string, unknown>>(
            `/api/v1/payment-transactions/statistics?startDate=${startDate}&endDate=${endDate}`
          )
          .catch(() => ({})),
        subscriptionPlanApi.list({ page: 0, size: 1000 }).catch(() => ({ content: [] })),
      ]);

      const statsObj = (statisticsData ?? {}) as Record<string, unknown>;

      // Status counts
      const activeCount = Number(statsObj.activeSubscriptions ?? 0);
      const trialCount = Number(statsObj.totalTrials ?? 0);
      const cancelledCount = Number(statsObj.cancelledSubscriptions ?? 0);
      const pausedCount = Number(statsObj.pausedSubscriptions ?? 0);
      const suspendedCount = Number(statsObj.suspendedSubscriptions ?? 0);

      const statusCounts: StatusCount[] = [
        { status: 'ACTIVE', count: activeCount },
        { status: 'TRIAL', count: trialCount },
        { status: 'CANCELLED', count: cancelledCount },
        { status: 'PAUSED', count: pausedCount },
        { status: 'SUSPENDED', count: suspendedCount },
      ].filter((s) => s.count > 0);

      // Plan distribution from fetched plans
      const planList = plans.content || [];
      const planCounts: Record<string, number> = {};
      planList.forEach((plan: SubscriptionPlan) => {
        const type = String(plan.planType ?? plan.displayName ?? 'Unknown');
        planCounts[type] = (planCounts[type] || 0) + 1;
      });
      const planDistribution: PlanDistribution[] = Object.entries(planCounts).map(([plan, count]) => ({
        plan,
        count,
      }));

      // Funnel data
      const convertedTrials = Number(statsObj.convertedTrials ?? 0);
      const funnelData = [
        { stage: 'Trial Started', count: trialCount },
        { stage: 'Became Active', count: convertedTrials },
        { stage: 'Cancelled', count: cancelledCount },
      ];

      // Recent events (from statistics if available, or placeholder)
      const recentEvents = ((statsObj.recentEvents ?? []) as SubscriptionEvent[]).slice(0, 20);

      // Average duration
      const avgDurationDays = Number(statsObj.averageSubscriptionDurationDays ?? 0);

      setData({ statusCounts, planDistribution, funnelData, recentEvents, avgDurationDays });
    } catch (error) {
      console.error('Error fetching subscription analytics:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load subscription analytics',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCSV = () => {
    if (data.statusCounts.length === 0 && data.planDistribution.length === 0) return;
    const rows = [
      ...data.statusCounts.map((s) => ({ Category: 'Status', Name: s.status, Count: s.count })),
      ...data.planDistribution.map((p) => ({ Category: 'Plan', Name: p.plan, Count: p.count })),
    ];
    exportToCSV(rows, 'subscription-analytics.csv');
  };

  const totalSubscriptions = useMemo(
    () => data.statusCounts.reduce((sum, s) => sum + s.count, 0),
    [data.statusCounts]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-8 w-8" />
            Subscription Analytics
          </h1>
          <p className="text-muted-foreground mt-2">
            Subscription lifecycle metrics and distribution
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <BrandedLoading message="Loading subscription analytics…" iconSize="sm" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Subscriptions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalSubscriptions}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Average Duration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.avgDurationDays} days</div>
                <p className="text-xs text-muted-foreground">For cancelled subscriptions</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Active Plans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.planDistribution.length}</div>
                <p className="text-xs text-muted-foreground">Distinct plan types</p>
              </CardContent>
            </Card>
          </div>

          {/* Status Donut + Plan Pie */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Subscription Status</CardTitle>
                <CardDescription>Active / Trial / Cancelled / Paused / Suspended</CardDescription>
              </CardHeader>
              <CardContent>
                {data.statusCounts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={data.statusCounts}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        label={({ status, count }) => `${status}: ${count}`}
                      >
                        {data.statusCounts.map((entry) => (
                          <Cell
                            key={entry.status}
                            fill={STATUS_COLORS[entry.status] || '#6b7280'}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No subscription data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Plan Distribution</CardTitle>
                <CardDescription>Number of plans by type</CardDescription>
              </CardHeader>
              <CardContent>
                {data.planDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={data.planDistribution}
                        dataKey="count"
                        nameKey="plan"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ plan, count }) => `${plan}: ${count}`}
                      >
                        {data.planDistribution.map((_, idx) => (
                          <Cell key={idx} fill={PLAN_COLORS[idx % PLAN_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No plan distribution data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Trial Conversion Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Trial Conversion Funnel</CardTitle>
              <CardDescription>Trial Started, Active, Cancelled</CardDescription>
            </CardHeader>
            <CardContent>
              {data.funnelData.some((f) => f.count > 0) ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.funnelData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="stage" width={120} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No funnel data available</div>
              )}
            </CardContent>
          </Card>

          {/* Recent Subscription Events */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Subscription Events</CardTitle>
              <CardDescription>Last 20 subscription changes</CardDescription>
            </CardHeader>
            <CardContent>
              {data.recentEvents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">User</th>
                        <th className="text-left py-2 px-3 font-medium">Plan</th>
                        <th className="text-left py-2 px-3 font-medium">Event</th>
                        <th className="text-left py-2 px-3 font-medium">Status</th>
                        <th className="text-left py-2 px-3 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentEvents.map((event) => (
                        <tr key={event.id} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3">{event.userEmail}</td>
                          <td className="py-2 px-3">{event.planName}</td>
                          <td className="py-2 px-3">{event.eventType}</td>
                          <td className="py-2 px-3">
                            <span
                              className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${STATUS_COLORS[event.status] || '#6b7280'}20`,
                                color: STATUS_COLORS[event.status] || '#6b7280',
                              }}
                            >
                              {event.status}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {new Date(event.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No recent subscription events. Events will appear here when the backend provides event data.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SubscriptionAnalyticsPage;
